/**
 * Shared condition formatting utilities
 * Used by both Grid and Flow modes for consistent condition display
 */

export interface FormattedCondition {
  icon: string;
  label: string;
  detail?: string;
}

/**
 * Extract a displayable detail string from condition parameters
 * Handles nested objects like { selection: { type: 'product', productId: 'xxx' } }
 */
function extractParamDetail(params: Record<string, any>): string | undefined {
  // Handle ProductVariantSelection format
  if (params.selection && typeof params.selection === 'object') {
    const sel = params.selection;
    if (sel.type === 'product' && sel.productId) {
      return `Product`;
    }
    if (sel.type === 'variants' && sel.variantIds?.length) {
      return `${sel.variantIds.length} variant${sel.variantIds.length > 1 ? 's' : ''}`;
    }
  }

  // Handle legacy productId/variantId format
  if (params.productId) {
    return params.variantId ? 'Variant' : 'Product';
  }

  // Handle amount/value
  if (params.amount !== undefined) {
    return `>${params.amount}`;
  }

  // Handle tag/tags
  if (params.tag) {
    return params.tag.substring(0, 15);
  }
  if (params.tags && Array.isArray(params.tags)) {
    return `${params.tags.length} tag${params.tags.length > 1 ? 's' : ''}`;
  }

  // Generic: find first primitive value
  for (const key of Object.keys(params)) {
    const value = params[key];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value).substring(0, 15);
    }
  }

  return undefined;
}

/**
 * Format condition for compact display
 * Supports multiple formats:
 * - Simple strings: "payment.success"
 * - Objects with operator: { operator: "cart.totalGreaterThan", params: {...} }
 * - JSONLogic: { "==": [...], ">": [...], etc. }
 * - Arrays: [rule1, rule2, ...]
 */
export function formatCondition(when: string | any): FormattedCondition {
  // Simple string condition
  if (typeof when === 'string') {
    const conditions: Record<string, FormattedCondition> = {
      // Payment conditions
      'payment.success': { icon: '✓', label: 'Payment OK' },
      'payment.failed': { icon: '✗', label: 'Payment Failed' },
      // Offer conditions
      'offer.accepted': { icon: '✓', label: 'Offer Accepted' },
      'offer.declined': { icon: '✗', label: 'Offer Declined' },
      // Main Order conditions (checkout order)
      'mainOrder.exists': { icon: '🛒', label: 'Main Order Exists' },
      'mainOrder.hasProduct': { icon: '📦', label: 'Main Order Has Product' },
      'mainOrder.totalGreaterThan': { icon: '💰', label: 'Main Order Value' },
      // Last Order conditions (most recent order)
      'lastOrder.exists': { icon: '🛍️', label: 'Last Order Exists' },
      'lastOrder.hasProduct': { icon: '📦', label: 'Last Order Has Product' },
      'lastOrder.totalGreaterThan': { icon: '💰', label: 'Last Order Value' },
      // Customer conditions
      'customer.hasTag': { icon: '🏷️', label: 'Has Tag' },
      'customer.hasTagPrefix': { icon: '🏷️', label: 'Tag Prefix' },
      'customer.hasAnyTag': { icon: '🏷️', label: 'Any Tag' },
      'customer.hasAllTags': { icon: '🏷️', label: 'All Tags' },
      // Generic
      always: { icon: '∞', label: 'Always' },
    };
    return conditions[when] || { icon: '≡', label: when };
  }

  // Object-based condition
  if (typeof when === 'object' && when !== null && !Array.isArray(when)) {
    const keys = Object.keys(when);
    if (keys.length === 0) return { icon: '≡', label: 'Conditional' };

    // OLD FORMAT: { operator: "...", params: {...} }
    if ('operator' in when && when.operator) {
      const operator = when.operator;
      const params = when.params;

      const customOperators: Record<string, FormattedCondition> = {
        // Payment conditions
        'payment.success': { icon: '✓', label: 'Payment OK' },
        'payment.failed': { icon: '✗', label: 'Payment Failed' },
        'payment.amountGreaterThan': { icon: '$', label: 'Payment Amount' },
        // Offer conditions
        'offer.accepted': { icon: '✓', label: 'Offer Accepted' },
        'offer.declined': { icon: '✗', label: 'Offer Declined' },
        'offer.acceptedWithId': { icon: '✓', label: 'Specific Offer' },
        // Main Order conditions (checkout order)
        'mainOrder.exists': { icon: '🛒', label: 'Main Order' },
        'mainOrder.hasProduct': { icon: '📦', label: 'Main Order Product' },
        'mainOrder.totalGreaterThan': { icon: '💰', label: 'Main Order Value' },
        // Last Order conditions (most recent)
        'lastOrder.exists': { icon: '🛍️', label: 'Last Order' },
        'lastOrder.hasProduct': { icon: '📦', label: 'Last Order Product' },
        'lastOrder.totalGreaterThan': { icon: '💰', label: 'Last Order Value' },
        // Customer conditions
        'customer.hasTag': { icon: '🏷️', label: 'Has Tag' },
        'customer.hasTagPrefix': { icon: '🏷️', label: 'Tag Prefix' },
        'customer.hasAnyTag': { icon: '🏷️', label: 'Any Tag' },
        'customer.hasAllTags': { icon: '🏷️', label: 'All Tags' },
      };

      const formatted = customOperators[operator] || { icon: '⚙', label: operator };

      // Extract key param for display
      if (params && typeof params === 'object') {
        const detail = extractParamDetail(params);
        if (detail) return { ...formatted, detail };
      }

      return formatted;
    }

    // NEW FORMAT: Custom operators with params (our shorthand)
    // E.g., { 'cart.totalGreaterThan': { amount: 100 } }
    const firstKey = keys[0];

    const customShorthandOperators: Record<string, FormattedCondition> = {
      // Payment conditions
      'payment.success': { icon: '✓', label: 'Payment OK' },
      'payment.failed': { icon: '✗', label: 'Payment Failed' },
      'payment.amountGreaterThan': { icon: '$', label: 'Payment Amount' },
      // Offer conditions
      'offer.accepted': { icon: '✓', label: 'Offer Accepted' },
      'offer.declined': { icon: '✗', label: 'Offer Declined' },
      'offer.acceptedWithId': { icon: '✓', label: 'Specific Offer' },
      // Main Order conditions (checkout order)
      'mainOrder.exists': { icon: '🛒', label: 'Main Order' },
      'mainOrder.hasProduct': { icon: '📦', label: 'Main Order Product' },
      'mainOrder.totalGreaterThan': { icon: '💰', label: 'Main Order Value' },
      // Last Order conditions (most recent)
      'lastOrder.exists': { icon: '🛍️', label: 'Last Order' },
      'lastOrder.hasProduct': { icon: '📦', label: 'Last Order Product' },
      'lastOrder.totalGreaterThan': { icon: '💰', label: 'Last Order Value' },
      // Customer conditions
      'customer.hasTag': { icon: '🏷️', label: 'Has Tag' },
      'customer.hasTagPrefix': { icon: '🏷️', label: 'Tag Prefix' },
      'customer.hasAnyTag': { icon: '🏷️', label: 'Any Tag' },
      'customer.hasAllTags': { icon: '🏷️', label: 'All Tags' },
    };

    if (customShorthandOperators[firstKey]) {
      const params = when[firstKey];
      const formatted = customShorthandOperators[firstKey];

      // Extract key param for display
      if (params && typeof params === 'object') {
        const detail = extractParamDetail(params);
        if (detail) return { ...formatted, detail };
      }

      return formatted;
    }

    // Standard JSONLogic operators
    const jsonLogicOps: Record<string, FormattedCondition> = {
      '==': { icon: '=', label: 'Equals' },
      '!=': { icon: '≠', label: 'Not Equal' },
      '>': { icon: '>', label: 'Greater' },
      '>=': { icon: '≥', label: 'Greater/Equal' },
      '<': { icon: '<', label: 'Less' },
      '<=': { icon: '≤', label: 'Less/Equal' },
      and: { icon: '∧', label: 'AND' },
      or: { icon: '∨', label: 'OR' },
      '!': { icon: '¬', label: 'NOT' },
      in: { icon: '∈', label: 'In Array' },
      some: { icon: '∃', label: 'Some' },
      all: { icon: '∀', label: 'All' },
    };

    if (jsonLogicOps[firstKey]) {
      return jsonLogicOps[firstKey];
    }

    // Fallback: show operator name
    return { icon: '⚙', label: firstKey };
  }

  // Array of rules (old format)
  if (Array.isArray(when) && when.length > 0) {
    return { icon: '≡', label: `${when.length} Rules` };
  }

  return { icon: '≡', label: 'Conditional' };
}

/**
 * Format condition for compact single-line display (used in tight spaces)
 */
export function formatConditionCompact(when: string | any): string {
  const formatted = formatCondition(when);
  return `${formatted.icon} ${formatted.label}${formatted.detail ? `: ${formatted.detail}` : ''}`;
}

